import { ChangeDetectionStrategy, Component, OnInit, DestroyRef, inject, signal, DOCUMENT } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { interval } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserSessionService, ReauthRequiredError } from '@shared/services/user-session.service';
import { APP_ENTITIES } from './config/app-urls';
import { InactivityService } from '@shared/services/inactivity.service';
import { NotificationsService } from '@shared/services/notifications.service';
import { SessionWarningModalComponent } from './components/session-warning-modal/session-warning-modal.component';
import { ErrorLogoutModalComponent } from './components/error-logout-modal/error-logout-modal.component';
import { SignupLogoutModalComponent } from './components/signup-logout-modal/signup-logout-modal.component';
import { ByoidProvisioningModalComponent } from './components/byoid-provisioning-modal/byoid-provisioning-modal.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    SessionWarningModalComponent,
    ErrorLogoutModalComponent,
    SignupLogoutModalComponent,
    ByoidProvisioningModalComponent
  ]
})
export class AppComponent implements OnInit {
  readonly isLoading = signal(true); // Start in loading state by default
  readonly showCountdown = signal(false);
  readonly showErrorLogout = signal(false);
  readonly showSignupLogout = signal(false);
  readonly showByoidProvisioning = signal(false);
  private _pendingErrorRedirectUrl = '';
  private _pendingSignupRedirectUrl = '';
  private _pendingByoidRedirectUrl = '';
  readonly faviconUrl = signal<string | null>(null);
  readonly logoUrl = signal<string | null>(null);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _initialPath: string = globalThis.location === undefined ? '/' : globalThis.location.pathname;

  // Poll for new notifications every 1 minute
  private readonly POLL_INTERVAL_MS = 60000;
  private _isPollingStarted = false;

  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _router = inject(Router);
  private readonly _userSessionService = inject(UserSessionService);
  private readonly _inactivityService = inject(InactivityService);
  private readonly _notificationsService = inject(NotificationsService);
  private readonly _document = inject(DOCUMENT);

  ngOnInit() {
    // Handle redirect observable for standalone MSAL (replaces MsalRedirectComponent in bootstrap)
    this._msalService.handleRedirectObservable().subscribe({
      error: (err) => console.error('[Auth] Redirect error:', err)
    });

    // Handle MSAL login failures and surface a friendly message on the login page
    this._msalBroadcastService.msalSubject$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((evt: EventMessage) => {
        if (evt?.eventType === EventType.LOGIN_FAILURE) {
          const error = evt?.error as unknown as Record<string, unknown> | undefined;
          const rawCode = error?.status ?? error?.errorCode;
          let errCode = '';
          if (typeof rawCode === 'object' && rawCode != null) {
            errCode = JSON.stringify(rawCode);
          } else if (rawCode != null) {
            errCode = String(rawCode as string | number | boolean);
          }
          console.error('[Auth] Login failure event received:', error);
          this.isLoading.set(false);
          try {
            sessionStorage.setItem('loginError', JSON.stringify({ error: 'loginFailed', code: errCode }));
          } catch (storageError) {
            console.error('[App] Failed to store login error in sessionStorage:', storageError);
          }
          this._router.navigateByUrl(`/login?error=loginFailed&code=${encodeURIComponent(errCode)}`);
        }
      });

    this._msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1), // Only take the first emission to prevent duplicate initialization
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        const accounts = this._msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
          // Check if this is a signup flow - redirect to account-setup instead of initializing session
          if (this.isSignupFlow()) {
            this.handleSignupCompletion();
            return;
          }

          // User is authenticated (either new login or existing session)
          this._msalService.instance.setActiveAccount(accounts[0]);

          this._userSessionService.initialize().pipe(timeout(15000)).subscribe({
            next: () => {
              this.isLoading.set(false);

              // Load branding assets (favicon and logo)
              this.loadBrandingAssets();

              // Start inactivity tracking after successful authentication
              this._inactivityService.start();

              // Start polling for notifications after successful authentication
              this.startNotificationPolling();

              if (this._router.url.startsWith('/login') || this._router.url === '/') {
                // If user initially requested a deep link, honor it; otherwise go to entities
                const target = (this._initialPath && this._initialPath !== '/' && !this._initialPath.startsWith('/login'))
                  ? this._initialPath
                  : APP_ENTITIES.toRoutePath();
                this._router.navigate([target]);
              }
            },
            error: (err) => {
              if (err instanceof ReauthRequiredError) {
                // Bootstrap provisioning was kicked off for this federated (BYOID) first-login
                // user. Tell them immediately and sign them out on confirm - this allows popup
                // logout to work since it will be triggered by user interaction.
                this.isLoading.set(false);
                this._pendingByoidRedirectUrl = '/login?accountSetup=true';
                this.showByoidProvisioning.set(true);
                return;
              }

              console.error('[Auth] Session initialization failed. Redirecting to login.', err);
              this.isLoading.set(false);
              const errCode = String(err?.status || '');

              // Extract error message for login page display
              let errorMessage = '';
              if (typeof err?.error === 'string') {
                errorMessage = err.error;
              } else if (err?.error?.message) {
                errorMessage = err.error.message;
              } else if (err?.message) {
                errorMessage = err.message;
              }

              const errorType = err?.status === 403 ? 'unauthorized' : 'initFailed';

              // Store error in sessionStorage for login page to display
              try {
                sessionStorage.setItem('loginError', JSON.stringify({
                  error: errorType,
                  code: errCode,
                  message: errorMessage
                }));
              } catch (storageError) {
                console.error('[App] Failed to store auth error in sessionStorage:', storageError);
              }

              // Build redirect URL with error params for login page
              const queryParams = new URLSearchParams({ error: errorType, code: errCode });
              if (errorMessage) queryParams.append('message', errorMessage);

              // Show error modal - user must click to sign out
              // This allows popup logout to work since it will be triggered by user interaction
              this._pendingErrorRedirectUrl = `/login?${queryParams.toString()}`;
              this.showErrorLogout.set(true);
            }
          });
        } else {
          // User is not authenticated
          this.isLoading.set(false);
          if (!this._router.url.startsWith('/login')) {
            this._router.navigate(['/login'], { queryParams: { error: 'unauthenticated' } });
          }
        }
      });

    // Inactivity handling: show countdown at warning, logout at timeout
    this._inactivityService.warning$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.showCountdown.set(true);
      });

    this._inactivityService.logout$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.handleLogout();
      });
  }

  continueSession() {
    this.showCountdown.set(false);
    this._inactivityService.resetTimer();
  }

  handleLogout() {
    this.showCountdown.set(false);
    this._userSessionService.logout();
  }

  handleErrorLogout() {
    this.showErrorLogout.set(false);
    this._userSessionService.logout(this._pendingErrorRedirectUrl);
  }

  handleSignupLogout() {
    this.showSignupLogout.set(false);
    // Use popup logout with full page reload to clear MSAL cache
    // forceReload: true prevents cached account from being reused on next sign-up
    this._userSessionService.logout(this._pendingSignupRedirectUrl, true, true);
  }

  handleByoidProvisioningLogout() {
    this.showByoidProvisioning.set(false);
    this._userSessionService.logout(this._pendingByoidRedirectUrl, true, true);
  }

  private startNotificationPolling(): void {
    // Prevent duplicate polling if already started
    // Set flag immediately to prevent race condition
    if (this._isPollingStarted) {
      return;
    }

    this._isPollingStarted = true;

    // Load notifications immediately
    this._notificationsService.loadNotifications();

    // Start polling for new notifications every minute
    interval(this.POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._notificationsService.loadNotifications();
      });
  }

  private loadBrandingAssets(): void {
    // Load branding configuration from window.__cwp_boot (set by portal-config endpoint)
    const globalConfig = (globalThis as typeof globalThis & { __cwp_boot?: Record<string, string> }).__cwp_boot;

    if (globalConfig?.faviconUrl) {
      this.faviconUrl.set(globalConfig.faviconUrl);
      this.setFavicon(globalConfig.faviconUrl);
    }
    if (globalConfig?.logoUrl) {
      this.logoUrl.set(globalConfig.logoUrl);
      // Store in window for use in components
      (globalThis as typeof globalThis & { __brandingLogo?: unknown }).__brandingLogo = globalConfig.logoUrl;
    }
  }

  private setFavicon(faviconUrl: string): void {
    try {
      // Find or create favicon link element
      let faviconLink = this._document.querySelector('link[rel="icon"]');

      if (!faviconLink) {
        faviconLink = this._document.createElement('link');
        faviconLink.setAttribute('rel', 'icon');
        this._document.head.appendChild(faviconLink);
      }

      faviconLink.setAttribute('href', faviconUrl);
    } catch (err) {
      console.error('Error setting favicon:', err);
    }
  }

  private isSignupFlow(): boolean {
    try {
      return sessionStorage.getItem('isSignupFlow') === 'true';
    } catch {
      return false;
    }
  }

  private handleSignupCompletion(): void {
    // Set active account before logout to prevent account selection prompt
    const accounts = this._msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this._msalService.instance.setActiveAccount(accounts[0]);
    }

    // Get the invitation email before clearing session storage
    let signupEmail = '';
    try {
      signupEmail = sessionStorage.getItem('invitationEmail') || '';
      sessionStorage.removeItem('isSignupFlow');
    } catch (storageError) {
      console.error('[App] Failed to read signup email from sessionStorage:', storageError);
    }

    // The user holds a freshly-issued token right now from the signup redirect - kick off
    // BYOID provisioning immediately instead of waiting for a subsequent sign-in to discover
    // the missing role. This saves a full extra sign-in/sign-out round trip. Failure here is
    // non-fatal: checkRoleAndInitialize() will retry the bootstrap call on the next sign-in.
    this._userSessionService.bootstrapEntraIdCreated()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        error: (err) => console.error('[Auth] Failed to bootstrap BYOID provisioning after signup:', err)
      });

    // Build redirect URL with email parameter for pre-filling login form
    const redirectParams = new URLSearchParams({ accountSetup: 'true' });
    if (signupEmail) {
      redirectParams.append('email', signupEmail);
    }

    // Show confirmation modal to user before logout
    // This allows popup logout to work since it will be triggered by user interaction
    this.isLoading.set(false);
    this._pendingSignupRedirectUrl = `/login?${redirectParams.toString()}`;
    this.showSignupLogout.set(true);
  }
}
